import * as preact from "preact";

import * as appStorePurchases from "appStorePurchases";
import * as deviceDetect from "deviceDetect";
import * as featureFlags from "featureFlags";
import * as genericModals from "genericModals";
import * as haptics from "haptics";
import * as notificationUtils from "notificationUtils";
import * as practiceUtils from "practiceUtils";
import * as premiumUpsellModal from "premiumUpsellModal";
import readlangClient from "readlangClient";
import * as readlangLocalStorage from "readlangLocalStorage";
import * as languageMap from "shared/languageMap";
import * as usernameValidation from "shared/usernameValidation";
import stores from "stores/stores";
import * as tts from "tts";
import EditDictionaryView from "views/EditDictionaryView";
import NotificationSettingsView from "views/NotificationSettingsView";
import TTSVoiceSettingsView from "views/TTSVoiceSettingsView";

type Props = {
  appRouter: any;
};

type State = {
  hasNewPracticeModes: boolean;
};

class SettingsPageView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.determineEligibilityForNewPractice();

    // Re-fetch user to ensure we have up-to-date data
    stores.user.fetch();

    // Prime browser voices so the Auto-Pronounce controls are visible to
    // free users on languages with device voices (the gate is tier-aware
    // and consults the browser voice cache).
    void tts
      .initialize(stores.user.currentLearningLanguage.value)
      .then(() => this.forceUpdate());
  }

  async determineEligibilityForNewPractice() {
    this.setState({
      hasNewPracticeModes: await practiceUtils.hasNewPracticeModes(stores.user),
    });
  }

  render() {
    const languageString =
      languageMap.map[stores.user.currentLearningLanguage.value].english;
    const firstLanguageString =
      languageMap.map[stores.user.firstLanguage.value].english;
    const reminderEmail = notificationUtils.has(
      "dailyReminder",
      stores.user.currentLearningLanguage.value
    );
    // Remove the following after running for a bit
    const newNotificationsView = true;

    const relevantFeatureFlags = featureFlags.userFlags.filter(
      (flag) =>
        flag.experimental &&
        (flag.availableTo === "free" ||
          (flag.availableTo === "premium" &&
            stores.user.hasPremiumAccess.value) ||
          (flag.availableTo === "god" && stores.user.isGod.value))
    );

    return (
      <div>
        <h2 class="spacing">General settings</h2>
        <table class="settingsTable">
          <tbody>
            {stores.user.hasPremiumAccess.value && (
              <tr>
                <td>
                  <input
                    type="checkbox"
                    checked={stores.user.hasFeature("contextTranslations")}
                    id="contextAwareTranslationsCheckbox"
                    onClick={() => {
                      stores.user.setFeature(
                        "contextTranslations",
                        !stores.user.hasFeature("contextTranslations")
                      );
                    }}
                  />
                </td>
                <td>
                  <label htmlFor="contextAwareTranslationsCheckbox">
                    Context aware translations{" "}
                    <small>
                      - takes into account the surrounding sentence for more
                      accurate translations
                    </small>
                  </label>
                </td>
              </tr>
            )}
            <tr>
              <td>
                <input
                  type="checkbox"
                  checked={stores.user.hasFeature("betterAIModel")}
                  id="betterAIModelCheckbox"
                  onClick={(event) => {
                    if (
                      !stores.user.hasFeature("betterAIModel") &&
                      !stores.user.hasPremiumPlusAccess.value
                    ) {
                      premiumUpsellModal.show({
                        title: "betterAIModel",
                        heading: "Upgrade to Premium Plus for better AI model",
                        showTitle: false,
                        plus: true,
                      });
                      event.preventDefault();
                      return;
                    }

                    stores.user.setFeature(
                      "betterAIModel",
                      !stores.user.hasFeature("betterAIModel")
                    );
                  }}
                />
              </td>
              <td>
                <label htmlFor="betterAIModelCheckbox">
                  Better AI model{" "}
                  <small>
                    Uses the advanced AI model for AI powered features
                    (including context aware translations and explanations)
                    instead of the standard model
                  </small>
                </label>
              </td>
            </tr>
            <tr>
              <td>
                <input
                  type="checkbox"
                  checked={stores.user.state.value.soundEffectsEnabled}
                  id="soundEffectsCheckbox"
                  onClick={() => {
                    stores.user.save({
                      soundEffectsEnabled:
                        !stores.user.state.value.soundEffectsEnabled,
                    });
                  }}
                />
              </td>
              <td>
                <label htmlFor="soundEffectsCheckbox">
                  Sound effects{" "}
                  <small>
                    - plays a sound when you get a word right or wrong
                  </small>
                </label>
              </td>
            </tr>
            {deviceDetect.isIOSApp() && (
              <tr>
                <td>
                  <input
                    type="checkbox"
                    checked={haptics.isOn()}
                    id="hapticsCheckbox"
                    onClick={() => {
                      haptics.setOn(!haptics.isOn());
                      this.forceUpdate();
                    }}
                  />
                </td>
                <td>
                  <label htmlFor="hapticsCheckbox">
                    Haptics{" "}
                    <small>
                      - vibrates gently as you translate words, answer
                      flashcards and reach goals
                    </small>
                  </label>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <TTSVoiceSettingsView
          key={stores.user.currentLearningLanguage.value}
          language={stores.user.currentLearningLanguage.value}
          heading={`${languageString} voices`}
          variant="settings"
        />
        <h2 class="spacing">Practice settings</h2>
        <h3>Flashcard mode</h3>
        <p>
          All flashcards start in passive mode ({languageString} -&gt;{" "}
          {firstLanguageString}) the first time you see them. This setting
          affects the subsequent appearances.{" "}
          {this.state.hasNewPracticeModes ? (
            <small>
              (Applies both to "Classic Flashcards" and "Mastery" practice. In
              "Mastery" mode, the "Passive Recall Only" setting will make new
              words and phrases default to passive.)
            </small>
          ) : (
            ""
          )}
        </p>
        <table class="settingsTable">
          <tbody>
            <tr>
              <td>
                <input
                  type="radio"
                  name="flashcardMode"
                  value="passive"
                  id="passiveModeRadio"
                  checked={stores.user.flashcardMode.value === "passive"}
                  onClick={() => stores.user.setFlashcardMode("passive")}
                />
              </td>
              <td>
                <label htmlFor="passiveModeRadio">
                  Passive Recall Only{" "}
                  <small>(easiest, only practice reading comprehension)</small>
                </label>
              </td>
            </tr>
            <tr>
              <td>
                <input
                  type="radio"
                  name="flashcardMode"
                  value="mixed"
                  id="mixedModeRadio"
                  checked={stores.user.flashcardMode.value === "mixed"}
                  onClick={() => stores.user.setFlashcardMode("mixed")}
                />
              </td>
              <td>
                <label htmlFor="mixedModeRadio">
                  Mix of Active and Passive Recall <small>(recommended)</small>
                </label>
              </td>
            </tr>
            <tr>
              <td>
                <input
                  type="radio"
                  name="flashcardMode"
                  value="active"
                  id="activeModeRadio"
                  checked={stores.user.flashcardMode.value === "active"}
                  onClick={() => stores.user.setFlashcardMode("active")}
                />
              </td>
              <td>
                <label htmlFor="activeModeRadio">Mainly Active Recall</label>
              </td>
            </tr>
          </tbody>
        </table>
        <h3>Review spacing</h3>
        <p>
          How long Readlang waits before scheduling a flashcard's next review on
          a future day. Wider spacing means longer gaps between repeats — if you
          find reviews too easy, widen the spacing to spend more of your
          practice time on new words. This doesn't change how cards repeat
          within a single practice session.
        </p>
        <table class="settingsTable">
          <tbody>
            {(
              [
                {
                  value: "closer",
                  label: "Closer",
                  detail: "repeats come back sooner",
                },
                {
                  value: "standard",
                  label: "Standard",
                  detail: "recommended",
                },
                {
                  value: "wider",
                  label: "Wider",
                  detail: "first repeat after ~2 days, gaps grow faster",
                },
                {
                  value: "widest",
                  label: "Widest",
                  detail: "first repeat after ~3 days, fewest repeats",
                },
              ] as const
            ).map(({ value, label, detail }) => {
              const id = `reviewPace-${value}`;
              const currentPace =
                stores.user.state.value.reviewPace ?? "standard";
              return (
                <tr>
                  <td>
                    <input
                      type="radio"
                      name="reviewPace"
                      value={value}
                      id={id}
                      checked={currentPace === value}
                      onClick={() => stores.user.save({ reviewPace: value })}
                    />
                  </td>
                  <td>
                    <label htmlFor={id}>
                      {label} <small>({detail})</small>
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {newNotificationsView ? (
          <>
            <h2 class="spacing">Daily reminders</h2>
            <NotificationSettingsView />
          </>
        ) : (
          <div>
            <h2 class="spacing">{languageString} email reminders</h2>

            <label>
              <input
                type="checkbox"
                class="reminderEmail"
                checked={reminderEmail}
              />{" "}
              Send Daily {languageString} Reminder Emails
              <small>
                {" "}
                - if you haven't read 500 words or practiced 10 flashcards yet
              </small>
            </label>
          </div>
        )}
        <h2 class="spacing">Custom {languageString} dictionary</h2>
        <p>
          This allows you to use any online {languageString} dictionary in
          Readlang's sidebar.
        </p>
        <EditDictionaryView key={stores.user.currentLearningLanguage.value} />
        {stores.user.state.value.activeLanguages &&
        stores.user.state.value.activeLanguages.length >= 2 ? (
          <>
            <h2 class="spacing">Active languages</h2>
            <p>
              These are your recently used languages and will be listed first in
              the language selector. Remove any you no longer need. Removed
              languages are also excluded from your{" "}
              <a href="/progress">Long Term Progress</a> report.
            </p>
            <div class="activeLanguagesList">
              {(stores.user.state.value.activeLanguages ?? [])
                .map((languageCode) => ({
                  code: languageCode,
                  language: languageMap.map[languageCode],
                }))
                .filter((item) => item.language) // Filter out invalid language codes
                .sort((a, b) =>
                  a.language.english.localeCompare(b.language.english)
                )
                .map(({ code: languageCode, language }) => {
                  return (
                    <button
                      type="button"
                      class="languageTag"
                      key={languageCode}
                      onClick={(event) => {
                        event.preventDefault();
                        this.removeActiveLanguage(languageCode);
                      }}
                      title={`Remove ${language.english}`}
                    >
                      {language.english}
                      <i class="icon icon-remove-sign"></i>
                    </button>
                  );
                })}
            </div>
          </>
        ) : null}
        {relevantFeatureFlags.length > 0 ? (
          <>
            <h2 class="spacing" id="experimentalFeatures">
              Experimental features
            </h2>
            <p>
              Enable or disable experimental features. This allows early access
              to features which may or may not stick around for the long term.
            </p>
            <table class="settingsTable">
              <tbody>
                {relevantFeatureFlags.map((flag) => (
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        class={flag.key}
                        checked={stores.user.hasFeature(flag.key)}
                        id={`featureFlag-${flag.key}`}
                        onClick={() => {
                          stores.user.setFeature(
                            flag.key,
                            !stores.user.hasFeature(flag.key)
                          );
                        }}
                      />
                    </td>
                    <td>
                      <label for={`featureFlag-${flag.key}`}>
                        <strong>{flag.key}</strong>{" "}
                        {flag.availableTo === "premium"
                          ? "(premium only)"
                          : null}{" "}
                        {flag.description}
                      </label>{" "}
                      {flag.forumLink ? (
                        <>
                          <small>
                            <a href={flag.forumLink} target="_blank">
                              Discuss on forum
                            </a>
                            .
                          </small>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}
        {stores.user.blockedUsers.value.length > 0 && (
          <>
            <h2 class="spacing">Blocked users</h2>
            <p>You won't see texts shared by these users.</p>
            <ul>
              {stores.user.blockedUsers.value.map((user) => (
                <li key={user._id}>
                  <a
                    href={usernameValidation.userProfilePath(
                      user.username,
                      user._id
                    )}
                  >
                    {user.username}
                  </a>{" "}
                  <button
                    onClick={() => stores.user.setBlocked(user._id, false)}
                  >
                    Unblock
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        {/*
          Always offered, including to auto-renewing subscribers: the guard
          that used to hide this from them existed to stop deletion stranding a
          live Subscription, and the delete route now cancels at Stripe first
          and refuses if that fails (atlas 00653). Tier 3b removed the cached
          `renewalMonths` it read.
        */}
        <h2 class="spacing">Your Readlang account</h2>
        <p>
          <a
            href="/api/exportAllData.txt"
            class="exportAccountDataLink"
            target="_blank"
            data-bypass="true"
          >
            Export all personal data stored in my Readlang account
          </a>
        </p>
        <p>
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void this.deleteAccount();
            }}
          >
            <i class="icon-warning-sign"></i> Delete my Readlang account along
            with all personal data
          </a>
        </p>
        <br />
        &nbsp;
      </div>
    );
  }

  removeActiveLanguage = (languageCode: string) => {
    const activeLanguages = stores.user.state.value.activeLanguages || [];
    const updatedLanguages = activeLanguages.filter(
      (lang) => lang !== languageCode
    );
    stores.user.save({ activeLanguages: updatedLanguages });
  };

  deleteAccount = async () => {
    if (await this.continueDespiteAppStoreSubscription()) {
      this.confirmDeleteAccount();
    }
  };

  /**
   * Deleting the account doesn't cancel what Apple bills for: an App Store
   * subscriber is told so first. Whether to go on deleting.
   */
  continueDespiteAppStoreSubscription = async () => {
    const { data: accountState } = await readlangClient.request<{
      type: string;
      renewsAutomatically?: boolean;
    }>("GET", "/api/stripe/accountState", {});
    if (
      accountState.type !== "appStoreSubscription" ||
      !accountState.renewsAutomatically
    ) {
      return true;
    }
    const inApp = deviceDetect.isIOSApp();
    return await new Promise<boolean>((resolve) => {
      genericModals.confirm({
        title: "Your App Store subscription",
        warning: true,
        message: inApp
          ? "Deleting your account doesn't cancel the Readlang Premium subscription you pay Apple for. Cancel it first in Manage Subscription."
          : "Deleting your account doesn't cancel the Readlang Premium subscription you pay Apple for. Cancel it first on your iPhone, in Settings → your name → Subscriptions.",
        buttons: [
          { name: "cancel", displayName: "Cancel" },
          ...(inApp
            ? [{ name: "manage", displayName: "Manage Subscription" }]
            : []),
          { name: "ok", displayName: "Delete anyway" },
        ],
        callback: (response) => {
          if (response === "manage") {
            void appStorePurchases.manageSubscriptions();
          }
          resolve(response === "ok");
        },
      });
    });
  };

  confirmDeleteAccount = () => {
    genericModals.confirm({
      title: "Permanently delete your Readlang account",
      warning: true,
      message:
        "Are you sure you want to **permanently delete** your account, including **all your " +
        "texts and words**?",
      callback: (response) => {
        if (response === "ok") {
          genericModals.confirm({
            title: "Seriously, there's no way back from this!",
            warning: true,
            message:
              "Are you absolutely sure you want to **permanently delete** your account, " +
              "including **all your texts and words**?",
            callback: (innerResponse) => {
              if (innerResponse === "ok") {
                readlangClient.request(
                  "DELETE",
                  "/api/user/" + stores.user.id.value,
                  {
                    confirmation: "yes i really want to delete this",
                  },
                  () => {
                    genericModals.alert({
                      message: "Account deleted! Logging out now...",
                    });
                    localStorage.clear();
                    readlangLocalStorage.clear(() => {
                      setTimeout(() => {
                        // The app's welcome screen, or the website's
                        window.location.href = deviceDetect.isIOSApp()
                          ? "/"
                          : "/landing-page";
                      }, 2000);
                    });
                  },
                  (error) => {
                    if (!error.alreadyNotified) {
                      genericModals.alert({
                        message: "Delete account failed",
                      });
                    }
                  }
                );
              }
            },
          });
        }
      },
    });
  };
}

export default SettingsPageView;
